import { useState, type FormEvent, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, BellRing, Check } from "lucide-react";
import { useAutoOpenPopup } from "@/hooks/useAutoOpenPopup";

interface NotifyMeDialogProps {
  trigger: ReactNode;
  productName?: string;
  source?: string;
  /** Controlled mode (optional) */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Accept Indian mobile numbers: 10 digits starting 6-9, optional +91 / 91 / 0 prefix
const PHONE_RE = /^(?:\+?91[\-\s]?|0)?[6-9]\d{9}$/;

export function NotifyMeDialog({
  trigger,
  productName,
  source = "notify-me",
  open,
  onOpenChange,
}: NotifyMeDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const actualOpen = isControlled ? open : internalOpen;
  const setOpen = (v: boolean) => {
    if (!isControlled) setInternalOpen(v);
    onOpenChange?.(v);
  };

  useAutoOpenPopup("notify", setOpen);

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setEmail("");
    setPhone("");
    setName("");
    setDone(false);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPhone = phone.trim().replace(/\s|-/g, "");

    if (!EMAIL_RE.test(trimmedEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (trimmedPhone && !PHONE_RE.test(trimmedPhone)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    setLoading(true);
    const { error } = await supabase.from("reservations").insert({
      email: trimmedEmail,
      name: name.trim() || null,
      phone: trimmedPhone || null,
      source: productName ? `${source}:${productName}` : source,
    });
    setLoading(false);

    if (error) {
      if (error.code === "23505") {
        setDone(true);
        toast.success("You're already on the list, we'll notify you at launch.");
        return;
      }
      toast.error("Something went wrong. Please try again.");
      return;
    }
    setDone(true);
    toast.success("You're on the list. We'll notify you the moment it's live.");
  };

  return (
    <Dialog
      open={actualOpen}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setTimeout(reset, 200);
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand/10 text-brand">
            {done ? <Check className="h-6 w-6" /> : <BellRing className="h-6 w-6" />}
          </div>
          <DialogTitle className="text-center font-display text-xl">
            {done ? "You're on the list" : "Notify me at launch"}
          </DialogTitle>
          <DialogDescription className="text-center">
            {done
              ? `We'll email${phone ? " & SMS" : ""} you the moment ${productName ?? "this product"} is live.`
              : `Drop your email${" "}and mobile, we'll ping you the moment ${productName ?? "it"} goes live. No spam.`}
          </DialogDescription>
        </DialogHeader>

        {!done && (
          <form onSubmit={onSubmit} className="mt-2 space-y-3">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              maxLength={120}
              className="w-full rounded-full border border-border bg-background px-5 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              required
              maxLength={320}
              autoComplete="email"
              className="w-full rounded-full border border-border bg-background px-5 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Mobile number (10 digits)"
              maxLength={20}
              inputMode="tel"
              autoComplete="tel"
              className="w-full rounded-full border border-border bg-background px-5 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            <Button
              type="submit"
              disabled={loading}
              className="h-12 w-full rounded-full bg-foreground text-sm font-semibold text-background hover:bg-foreground/90"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Notify me at launch"
              )}
            </Button>
            <p className="pt-1 text-center text-[11px] text-muted-foreground">
              We'll only message you about this launch.
            </p>
          </form>
        )}

        {done && (
          <Button
            onClick={() => setOpen(false)}
            className="mt-2 h-12 w-full rounded-full bg-foreground text-sm font-semibold text-background hover:bg-foreground/90"
          >
            Done
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
